import { useMemo, useState } from 'react';
import GrowChart from './GrowChart';
import { IconButton } from '@mui/material';
import { Tune as TuneIcon } from '@mui/icons-material';
import { t } from 'utilities/TranslationUtils';
import { darkTooltip, calculatePercentYAxisRange, isBinaryOnTargetKind } from 'utilities/chartUtils';
import { kickNoun } from 'utilities/kickUtils';
import { SessionKindGroupName } from 'types/models/SessionsPageData';
import { TrendGroupBy, getWeekKey } from './playerPerformanceUtils';

export type { TrendGroupBy };


export interface OnTargetTrendPoint {
  sessionId: string;
  capturedAtUtc: string;
  onTargetPct: number;
  total: number;
  onTarget: number;
}

interface AggregatedPoint {
  key: string;
  label: string;
  capturedAtUtc: string;
  total: number;
  onTarget: number;
  onTargetPct: number;
  sessionCount: number;
}


function aggregatePoints(points: OnTargetTrendPoint[], groupBy: TrendGroupBy): AggregatedPoint[] {
  if (groupBy === 'sessions') {
    return points.map((p) => {
      const d = new Date(p.capturedAtUtc);
      const label = d.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return {
        key: p.sessionId,
        label,
        capturedAtUtc: p.capturedAtUtc,
        total: p.total,
        onTarget: p.onTarget,
        onTargetPct: p.onTargetPct,
        sessionCount: 1,
      };
    });
  }

  const groups = new Map<string, { label: string; capturedAtUtc: string; total: number; onTarget: number; sessionCount: number }>();

  for (const p of points) {
    let key: string;
    let label: string;

    if (groupBy === 'weeks') {
      ({ key, label } = getWeekKey(p.capturedAtUtc));
    } else if (groupBy === 'months') {
      key = p.capturedAtUtc.slice(0, 7); // YYYY-MM
      const d = new Date(p.capturedAtUtc);
      label = d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    } else if (groupBy === 'quarters') {
      const d = new Date(p.capturedAtUtc);
      const q = Math.floor(d.getMonth() / 3) + 1;
      key = `${d.getFullYear()}-Q${q}`;
      label = t('playerPerformance.trendQuarterLabel', { quarter: q, year: d.getFullYear() });
    } else {
      // years
      key = p.capturedAtUtc.slice(0, 4);
      label = key;
    }

    const existing = groups.get(key) ?? { label, capturedAtUtc: p.capturedAtUtc, total: 0, onTarget: 0, sessionCount: 0 };
    existing.total += p.total;
    existing.onTarget += p.onTarget;
    existing.sessionCount += 1;
    if (p.capturedAtUtc > existing.capturedAtUtc) existing.capturedAtUtc = p.capturedAtUtc;
    groups.set(key, existing);
  }

  return Array.from(groups.entries())
    .map(([key, g]) => ({
      key,
      label: g.label,
      capturedAtUtc: g.capturedAtUtc,
      total: g.total,
      onTarget: g.onTarget,
      onTargetPct: g.total > 0 ? (g.onTarget / g.total) * 100 : 0,
      sessionCount: g.sessionCount,
    }))
    .sort((a, b) => a.capturedAtUtc.localeCompare(b.capturedAtUtc));
}

interface OnTargetTrendProps {
  points: OnTargetTrendPoint[];
  groupBy: TrendGroupBy;
  kind?: SessionKindGroupName;
  hideTimelineControl?: boolean;
}

const OnTargetTrend: React.FunctionComponent<OnTargetTrendProps> = ({
  points, groupBy, kind, hideTimelineControl,
}) => {
  const [showDataZoom, setShowDataZoom] = useState(false);
  const aggregated = useMemo(() => aggregatePoints(points, groupBy), [points, groupBy]);
  const shown = aggregated;

  // Binary kinds (Corner/ThrowIn/GK) show an average success %, not an "X of Y" count.
  const isBinaryKind = kind != null && isBinaryOnTargetKind(kind);
  const onTargetTooltipLine = (pt: AggregatedPoint, pct: string): string =>
    isBinaryKind
      ? t('playerPerformance.trendOnTargetLabelAvg', { pct, total: pt.total, noun: kickNoun(kind) })
      : t('playerPerformance.trendOnTargetLabel', { onTarget: Math.round(pt.onTarget), total: pt.total, noun: kickNoun(kind), pct });

  const controlsBar = hideTimelineControl ? null : (
    <div className='pp-chart-controls' style={{ position: 'absolute', right: 0, top: '-36px', zIndex: 10, marginRight: '-5px' }}>
      <div style={{ flex: 1 }} />
      <IconButton
        size='small'
        onClick={() => setShowDataZoom((v) => !v)}
        title={showDataZoom ? 'Hide timeline controls' : 'Show timeline controls'}
        sx={{ color: showDataZoom ? '#34D399' : '#9CA3AF' }}
      >
        <TuneIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </div>
  );

  if (groupBy !== 'sessions') {
    if (shown.length < 1) return <div className='pp-trend-empty'>{t('playerPerformance.noSessions')}</div>;

    const yAxisRange = calculatePercentYAxisRange(shown.map(p => p.onTargetPct));

    const option = {
      tooltip: {
        ...darkTooltip('item'),
        formatter: (params: { name: string; value: number; dataIndex: number }) => {
          const pt = shown[params.dataIndex];
          return [
            `<b>${pt.label}</b>`,
            onTargetTooltipLine(pt, params.value.toFixed(1)),
            t('playerPerformance.trendSessionsLabel', { count: pt.sessionCount }),
          ].join('<br/>');
        },
      },
      grid: { top: 30, bottom: showDataZoom ? 110 : 56, left: 36, right: 8, containLabel: false },
      xAxis: {
        type: 'category',
        data: shown.map(p => p.label),
        axisLabel: { fontSize: 11, color: '#6B7280', rotate: shown.length > 6 ? 30 : 0 },
        axisLine: { lineStyle: { color: '#E5E7EB' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        // Always floor at 0% so low values (e.g. 5%) are never clipped below the visible axis range.
        min: 0,
        max: yAxisRange.max,
        axisLabel: { fontSize: 10, color: '#9CA3AF', formatter: (v: number) => `${Math.round(v)}%` },
        splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [{
        type: 'bar',
        data: shown.map(p => p.onTargetPct),
        itemStyle: { color: '#34D399', borderRadius: [3, 3, 0, 0] },
        label: {
          show: true, position: 'top',
          formatter: (p: { value: number }) => `${p.value.toFixed(0)}%`,
          fontSize: 11, color: '#374151',
        },
      }],
      dataZoom: [
        {
          type: 'slider',
          show: showDataZoom,
          xAxisIndex: [0],
          start: Math.max(0, 100 - (1000 / Math.max(1, shown.length))),
          end: 100,
          textStyle: { color: '#6B7280', fontSize: 10 },
          handleStyle: { color: '#34D399', borderColor: '#34D399' },
          fillerColor: 'rgba(52, 211, 153, 0.1)',
          moveOnMouseMove: true,
          realtime: true,
          showDetail: true,
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: Math.max(0, 100 - (1000 / Math.max(1, shown.length))),
          end: 100,
        },
      ],
    };

    return (
      <>
        <div style={{ position: 'relative' }}>
          {controlsBar}
        </div>
        <div className='pp-trend-fill' data-test='pp-ontarget-trend'>
          <GrowChart option={option} />
        </div>
      </>
    );
  }

  if (shown.length < 1) {
    return <div className='pp-trend-empty'>{t('playerPerformance.noSessions')}</div>;
  }

  const yAxisRangeSessions = calculatePercentYAxisRange(shown.map(p => p.onTargetPct));

  const sessionsOption = {
    tooltip: {
      ...darkTooltip('item'),
      formatter: (params: { dataIndex: number }) => {
        const pt = shown[params.dataIndex];
        return [
          `<b>${pt.label}</b>`,
          onTargetTooltipLine(pt, pt.onTargetPct.toFixed(1)),
          t('playerPerformance.trendSessionsLabel', { count: pt.sessionCount }),
        ].join('<br/>');
      },
    },
    grid: { top: 8, bottom: showDataZoom ? 110 : 56, left: 36, right: 8, containLabel: false },
    xAxis: {
      type: 'category' as const,
      data: shown.map((p) =>
        new Date(p.capturedAtUtc).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      ),
      axisLabel: { fontSize: 10, color: '#6B7280', rotate: shown.length > 6 ? 30 : 0 },
      axisLine: { lineStyle: { color: '#E5E7EB' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value' as const,
      // Always floor at 0% so low values (e.g. 5%) are never clipped below the visible axis range.
      min: 0,
      max: yAxisRangeSessions.max,
      axisLabel: { fontSize: 10, color: '#9CA3AF', formatter: (v: number) => `${Math.round(v)}%` },
      splitLine: { lineStyle: { color: '#E5E7EB', opacity: 0.5 } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'line' as const,
        data: shown.map((p) => p.onTargetPct),
        smooth: false,
        symbol: 'circle',
        symbolSize: 7,
        lineStyle: { color: '#34D399', width: 2 },
        itemStyle: { color: '#34D399' },
        areaStyle: {
          color: {
            type: 'linear' as const,
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(52,211,153,0.28)' },
              { offset: 1, color: 'rgba(52,211,153,0.00)' },
            ],
          },
        },
      },
    ],
    dataZoom: [
      {
        type: 'slider',
        show: showDataZoom,
        xAxisIndex: [0],
        start: Math.max(0, 100 - (1000 / Math.max(1, shown.length))),
        end: 100,
        textStyle: { color: '#6B7280', fontSize: 10 },
        handleStyle: { color: '#34D399', borderColor: '#34D399' },
        fillerColor: 'rgba(52, 211, 153, 0.1)',
        moveOnMouseMove: true,
        realtime: true,
        showDetail: true,
      },
      {
        type: 'inside',
        xAxisIndex: [0],
        start: Math.max(0, 100 - (1000 / Math.max(1, shown.length))),
        end: 100,
      },
    ],
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        {controlsBar}
      </div>
      <div className='pp-trend-fill' data-test='pp-ontarget-trend'>
        <GrowChart option={sessionsOption} />
      </div>
    </>
  );
};

export default OnTargetTrend;
